import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { BrowserMultiFormatReader, IScannerControls } from '@zxing/browser';
import { createMembershipBarcodeHints } from '../../../../shared/utils/barcode-decode-hints.util';
import { tryDecodeBarcodeFromImageFile } from '../../../../shared/utils/decode-barcode-from-image.util';
import { decodeQrFromImageFile } from '../../../../shared/utils/decode-qr-from-image.util';
import { getIsMobileOrTablet } from '../../../../shared/utils/device.util';
import { isCameraPhotoOptionsError } from '../../../../shared/utils/error.util';
import {
  getCameraAccessErrorMessage,
  isCameraApiAvailable,
  isTorchSupported,
  requestBarcodeCameraStream,
  setTorchEnabled,
  stopMediaStream,
} from '../../../../shared/utils/camera-stream.util';

/**
 * ZXing waits this long between decode attempts. Its 500ms default gives a
 * hand-held label roughly two chances per second, and every frame that lands
 * mid-motion is wasted; 100ms keeps the scanner sampling while the user lines
 * the barcode up.
 */
const DELAY_BETWEEN_SCAN_ATTEMPTS_MS = 100;

@Component({
  selector: 'app-vendor-membership-barcode-scanner-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './vendor-membership-barcode-scanner-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./vendor-membership-barcode-scanner-modal.component.scss'],
})
export class VendorMembershipBarcodeScannerModalComponent implements OnChanges, OnDestroy {
  @Input() isVisible = false;
  @Input() isProcessing = false;
  /** Skip live camera and show the file-upload panel (card "Or upload" path). */
  @Input() uploadOnly = false;
  /**
   * CWS-1914: what is being scanned. The reader already accepts QR alongside the membership
   * symbologies (MEMBERSHIP_BARCODE_FORMATS), so this only decides what the copy says — an
   * attendant pointed at a prepaid QR should not be told to line up a membership barcode.
   */
  @Input() scanSubject: 'membership' | 'prepaid' = 'membership';
  /**
   * Optional stream started from the click that opened this modal.
   * Android WebView often denies getUserMedia unless it runs in that gesture.
   */
  @Input() cameraStreamPromise: Promise<MediaStream> | null = null;

  @Output() close = new EventEmitter<void>();
  @Output() scanned = new EventEmitter<string>();
  /**
   * An uploaded photo held a QR the reader could not read -- damaged, blurred, glared or
   * decorative -- rather than no code at all.
   *
   * The distinction matters because the two look identical to `tryDecodeBarcodeFromImageFile`,
   * which returns null for both. The app shell has told these apart since CWS-1908 and offers
   * the redemption code for an unreadable QR; this flow could not, so the same photo that
   * produced "type the code instead" from the navbar produced "No barcode found in that
   * image" here. Optional: a host that does not bind this keeps today's behaviour exactly.
   */
  @Output() qrUnreadable = new EventEmitter<void>();

  @ViewChild('video', { static: false }) videoRef?: ElementRef<HTMLVideoElement>;
  @ViewChild('fileInput', { static: false }) fileInputRef?: ElementRef<HTMLInputElement>;
  @ViewChild('captureInput', { static: false }) captureInputRef?: ElementRef<HTMLInputElement>;

  errorMessage = '';
  isStartingCamera = false;
  isDecodingUpload = false;
  isTorchAvailable = false;
  isTorchOn = false;

  private reader: BrowserMultiFormatReader | null = null;
  private scannerControls: IScannerControls | null = null;
  private activeStream: MediaStream | null = null;
  private scanLocked = false;
  private startGeneration = 0;

  constructor(private cdr: ChangeDetectorRef) {}

  get supportsCameraScan(): boolean {
    return !this.uploadOnly && getIsMobileOrTablet();
  }

  get supportsLiveCameraPreview(): boolean {
    return this.supportsCameraScan;
  }

  get isPrepaidScan(): boolean {
    return this.scanSubject === 'prepaid';
  }

  /** What the attendant is pointing the camera at, for the copy. */
  get subjectNoun(): string {
    return this.isPrepaidScan ? 'redemption QR code' : 'membership barcode';
  }

  get title(): string {
    if (this.isPrepaidScan) {
      return this.supportsCameraScan ? 'Scan redemption QR' : 'Upload redemption QR';
    }
    return this.supportsCameraScan ? 'Scan membership barcode' : 'Upload membership barcode';
  }

  get hintText(): string {
    const outcome = this.isPrepaidScan
      ? 'We will open the prepaid wash book automatically.'
      : 'We will look up the active subscription automatically.';
    if (this.supportsLiveCameraPreview) {
      return `Fill the guide with the ${this.isPrepaidScan ? 'QR code' : 'barcode'} and hold steady. ${outcome}`;
    }
    if (this.supportsCameraScan) {
      return `Take a photo of the ${this.subjectNoun}. ${outcome}`;
    }
    return `Upload a photo of the ${this.subjectNoun}. ${outcome}`;
  }

  get uploadButtonLabel(): string {
    const noun = this.isPrepaidScan ? 'QR code' : 'barcode';
    return this.supportsCameraScan ? `Take photo of ${noun}` : `Choose ${noun} image`;
  }

  get isBusy(): boolean {
    return this.isProcessing || this.isDecodingUpload || this.isStartingCamera;
  }

  get hideUploadControls(): boolean {
    return this.isProcessing || this.isDecodingUpload;
  }

  get loaderMessage(): string {
    if (this.isProcessing) {
      return this.isPrepaidScan ? 'Opening prepaid wash book…' : 'Looking up membership…';
    }
    if (this.isDecodingUpload) {
      return this.isPrepaidScan ? 'Reading QR code…' : 'Reading barcode…';
    }
    return 'Starting camera…';
  }

  get showCameraFallbackActions(): boolean {
    return this.supportsLiveCameraPreview && !!this.errorMessage && !this.isBusy;
  }

  get showTorchToggle(): boolean {
    return this.isTorchAvailable && !this.errorMessage && !this.hideUploadControls;
  }

  get torchLabel(): string {
    return this.isTorchOn ? 'Turn off light' : 'Turn on light';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isVisible']) {
      if (this.isVisible) {
        this.scanLocked = false;
        this.errorMessage = '';
        this.isDecodingUpload = false;
        if (this.supportsLiveCameraPreview) {
          setTimeout(() => {
            void this.startScanner();
          }, 0);
        } else {
          this.stopScanner();
        }
      } else {
        this.stopScanner();
        this.clearFileInputs();
      }
    }
  }

  ngOnDestroy(): void {
    this.stopScanner();
  }

  onClose(): void {
    this.close.emit();
  }

  onOverlayClick(event: Event): void {
    if (event.target === event.currentTarget) {
      this.onClose();
    }
  }

  onUploadClick(): void {
    if (this.isProcessing || this.isDecodingUpload) {
      return;
    }
    this.fileInputRef?.nativeElement?.click();
  }

  onCaptureClick(): void {
    if (this.isProcessing || this.isDecodingUpload) {
      return;
    }
    this.captureInputRef?.nativeElement?.click();
  }

  onRetryCameraClick(): void {
    if (this.isBusy || !this.supportsLiveCameraPreview) {
      return;
    }
    this.errorMessage = '';
    void this.startScanner({ fromUserGesture: true });
  }

  async onToggleTorchClick(): Promise<void> {
    if (!this.isTorchAvailable) {
      return;
    }
    this.isTorchOn = await setTorchEnabled(this.activeStream, !this.isTorchOn);
    this.cdr.markForCheck();
  }

  async onBarcodeFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    this.clearFileInputs();

    if (!file || this.isProcessing || this.isDecodingUpload) {
      return;
    }

    this.errorMessage = '';
    this.isDecodingUpload = true;
    this.scanLocked = true;
    this.stopScanner();

    try {
      const decoded = await tryDecodeBarcodeFromImageFile(file);
      if (!decoded) {
        // Before calling it "no barcode", ask the dedicated QR reader whether a QR was
        // there at all. BrowserMultiFormatReader simply throws on an unreadable one, so
        // without this second pass an invalid prepaid QR is indistinguishable from a
        // photo of a wall -- and the attendant is told to find another photo when what
        // they actually need is the code from the customer's SMS.
        const asQr = await decodeQrFromImageFile(file);
        if (asQr.kind === 'unreadable' && this.qrUnreadable.observed) {
          this.scanLocked = false;
          this.qrUnreadable.emit();
          return;
        }
        this.errorMessage = 'No barcode found in that image. Try another photo.';
        this.scanLocked = false;
        if (this.isVisible && this.supportsLiveCameraPreview) {
          setTimeout(() => {
            void this.startScanner();
          }, 0);
        }
        return;
      }
      this.scanned.emit(decoded);
    } finally {
      this.isDecodingUpload = false;
    }
  }

  private async startScanner(options?: { fromUserGesture?: boolean }): Promise<void> {
    if (!this.supportsLiveCameraPreview) {
      return;
    }

    if (!isCameraApiAvailable()) {
      this.errorMessage = getCameraAccessErrorMessage(
        Object.assign(new Error('CAMERA_UNAVAILABLE'), { name: 'NotSupportedError' })
      );
      return;
    }

    const generation = this.startGeneration + 1;
    this.startGeneration = generation;
    this.isStartingCamera = true;
    this.releaseScannerControls();
    this.reader = new BrowserMultiFormatReader(createMembershipBarcodeHints(), {
      tryPlayVideoTimeout: 10000,
      delayBetweenScanAttempts: DELAY_BETWEEN_SCAN_ATTEMPTS_MS,
    });

    let stream: MediaStream | null = null;
    try {
      const video = this.videoRef?.nativeElement;
      if (!video) {
        this.errorMessage = 'Camera preview could not be started.';
        return;
      }

      stream = await this.resolveCameraStream(options?.fromUserGesture === true);
      if (generation !== this.startGeneration || !this.isVisible) {
        if (!this.isVisible) {
          stopMediaStream(stream);
        }
        return;
      }

      this.scannerControls = await this.reader.decodeFromStream(stream, video, (result, error) => {
        if (this.scanLocked || !this.isVisible || this.isProcessing || this.isDecodingUpload) {
          return;
        }
        if (result?.getText()) {
          this.scanLocked = true;
          this.scanned.emit(result.getText());
        }
        if (error && error.name !== 'NotFoundException') {
          // ZXing emits NotFoundException while searching — ignore.
        }
      });
      this.activeStream = stream;
      this.isTorchAvailable = isTorchSupported(stream);
    } catch (error) {
      if (!this.isVisible || generation !== this.startGeneration) {
        stopMediaStream(stream);
        return;
      }
      stopMediaStream(stream);
      if (isCameraPhotoOptionsError(error)) {
        return;
      }
      this.errorMessage = getCameraAccessErrorMessage(error);
    } finally {
      if (generation === this.startGeneration) {
        this.isStartingCamera = false;
      }
      this.cdr.markForCheck();
    }
  }

  private async resolveCameraStream(fromUserGesture: boolean): Promise<MediaStream> {
    if (!fromUserGesture && this.cameraStreamPromise) {
      return this.cameraStreamPromise;
    }
    return requestBarcodeCameraStream();
  }

  private stopScanner(): void {
    this.startGeneration += 1;
    this.isStartingCamera = false;
    this.releaseScannerControls();
    this.releaseVideoStream();
  }

  private releaseScannerControls(): void {
    const controls = this.scannerControls;
    this.scannerControls = null;
    this.reader = null;

    try {
      const stopResult = controls?.stop() as void | Promise<void>;
      if (stopResult && typeof stopResult.then === 'function') {
        void stopResult.catch(() => undefined);
      }
    } catch {
      // Chrome Android + ZXing can throw setPhotoOptions when disabling torch.
    }
  }

  private releaseVideoStream(): void {
    const video = this.videoRef?.nativeElement;
    const stream = video?.srcObject as MediaStream | null | undefined;
    stopMediaStream(stream);
    stopMediaStream(this.activeStream);
    this.activeStream = null;
    this.isTorchAvailable = false;
    this.isTorchOn = false;
    if (video) {
      video.srcObject = null;
    }
  }

  private clearFileInputs(): void {
    if (this.fileInputRef?.nativeElement) {
      this.fileInputRef.nativeElement.value = '';
    }
    if (this.captureInputRef?.nativeElement) {
      this.captureInputRef.nativeElement.value = '';
    }
  }
}
